import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** The top-level view-mode surfaces of the app. */
export type AppMode = 'standard' | 'code' | 'writing' | 'image';

/** Writing-mode sub-mode, sent to the backend as the `mode` field. */
export type WritingMode = 'review' | 'rewrite' | 'generate';

@Injectable({
  providedIn: 'root'
})
export class ModeService {
  private readonly BASE_URL = 'https://figpt.org';
  private currentMode: string = 'standard';
  private modeSubject = new BehaviorSubject<string>('standard');

  currentMode$ = this.modeSubject.asObservable();

  constructor() {
    // Ensure standard mode is set on initialization
    this.setMode('standard');
  }

  /**
   * Non-streaming endpoint for the given mode.
   * Document RAG (file upload in general chat) still wins when files are present.
   */
  getEndpoint(hasFiles: boolean = false): string {
    if (hasFiles) {
      return `${this.BASE_URL}/questions/documentrag`;
    }

    // Only consider actual modes, not settings
    const actualMode = this.currentMode === 'temperature' || this.currentMode === 'context'
      ? 'standard'
      : this.currentMode;

    switch (actualMode) {
      case 'standard':
        return `${this.BASE_URL}/questions/general`;
      case 'writing':
        return this.getWriteEndpoint();
      case 'code':
        return this.getCodeEndpoint();
      default:
        return `${this.BASE_URL}/questions/general`;
    }
  }

  /** SSE streaming endpoint — used by text-only general chat. */
  getStreamEndpoint(): string {
    return `${this.BASE_URL}/questions/stream`;
  }

  /** Code assistant endpoint. Body: { question, uuid, language?, code_context? } */
  getCodeEndpoint(): string {
    return `${this.BASE_URL}/questions/code`;
  }

  /** Writing assistant (text/paste) endpoint. Body: { question, uuid, document_text, mode } */
  getWriteEndpoint(): string {
    return `${this.BASE_URL}/questions/write`;
  }

  /** Writing assistant file-upload endpoint. Multipart: file, question, uuid, mode */
  getWriteUploadEndpoint(): string {
    return `${this.BASE_URL}/questions/write/upload`;
  }

  /** Vision endpoint. Body: { question, uuid, image_b64, mimetype, temperature } */
  getVisionEndpoint(): string {
    return `${this.BASE_URL}/questions/vision`;
  }

  /** Universal file ingest. Multipart: file, uuid, question.
   *  Uses the /questions/-prefixed alias because nginx only proxies /questions/* and /api/*. */
  getIngestEndpoint(): string {
    return `${this.BASE_URL}/questions/ingest`;
  }

  /** Chart renderer. Body: { question, uuid }. Returns { message, chart_b64, chart_spec }. */
  getChartEndpoint(): string {
    return `${this.BASE_URL}/questions/chart`;
  }

  /**
   * Image generation. Body: { prompt, uuid }. Expected to return
   * { status, image_b64, message? }. Uses the /questions/ prefix so it's reachable
   * through the nginx proxy once the backend adds the endpoint.
   */
  getImageGenEndpoint(): string {
    return `${this.BASE_URL}/questions/image`;
  }

  getFeedbackEndpoint(): string {
    return `${this.BASE_URL}/feedback`;
  }

  getCurrentMode(): string {
    return this.currentMode;
  }

  /** Normalised view mode (settings like context/temperature collapse to 'standard'). */
  getAppMode(): AppMode {
    if (this.currentMode === 'code') return 'code';
    if (this.currentMode === 'writing') return 'writing';
    if (this.currentMode === 'image') return 'image';
    return 'standard';
  }

  setMode(mode: string): void {
    this.currentMode = mode;
    this.modeSubject.next(mode);
  }
}
